"use client";
import { Card } from "@cmp-src/data-display/Card/Card";
import { Heading } from "@cmp-src/data-display/Heading/Heading";
import { ResourceLabel } from "@cmp-src/labels/ResourceLabel/ResourceLabel";
import { TacticLoader } from "@cmp-src/loaders/TacticLoader/TacticLoader";
import { Button } from "@cmp-src/primitives/button";
import { useAskTessa } from "@cmp-src/tessa/AskTessaProvider";
import {
  AiChatToolShowReservationInfoOutput,
  AiChatToolShowReservationInfoPart,
  CreateWorkplaceRequestParams,
  ISchedule,
  IUserFull,
  Office,
  TessaAiChatPart,
  TessaChatMessage,
  WorkplaceRequestElement
} from "@gettactic/api";
import { cn, logError, useBooleanState } from "@gettactic/helpers";
import { dateStringToLocalTime } from "@gettactic/helpers/src/date-time/dateStringToLocalTime";
import { WorkplaceRequestReserveFormData } from "@gettactic/helpers/src/reserve/mapWorkplaceRequestToReserveFormData";
import {
  useAuthenticated,
  useI18n,
  useTacticHooks,
  useWorkplaceRequestsAllowed
} from "@gettactic/hooks";
import React, { useRef } from "react";
import { toast } from "react-hot-toast";
import { CheckInCardOnWeb } from "@/features/dashboard/components/CheckInCardOnWeb";
import { MapWithMiniOfficePhoto } from "@/features/dashboard/components/DashboardEventDetails/MapWithMiniOfficePhoto";
import { invalidateReservationQueries } from "@/features/reserve/components/Reserve/helpers";
import { ReserveForm } from "@/features/reserve/components/Reserve/ReserveForm";
import { useReserveModal } from "@/features/reserve/hooks/useReserveModal";
import { useReserve } from "@/features/reserve/providers/context";
import {
  noParkingResource,
  OnlineMeetingProvider,
  saveReservations
} from "@/features/reserve/utils/utils-reserve";
import { useScheduleById } from "@/features/settings/components/WorkplaceRequestsPage/components/RequestDetailsModal/ScheduleInfoCard";
import { ChatBotToolCard } from "@/features/tessa/components/ChatBotToolCard";
import {
  UseTessaMessageStateReturn,
  useTessaMessageState
} from "@/features/tessa/components/utils/useTessaMessageState";
import {
  TessaMessageRendererProps,
  TessaResponseMessages
} from "@/features/tessa/types";
import { useWorkplaceRequestMutations } from "@/features/workplace-requests/hooks/useWorkplaceRequestMutations";
import { WorkplaceRequestsRef } from "@/types/workplace-requests";

const getRecurringString = (reservation: Reservation) => {
  let recurring: string | undefined;
  if (reservation.recurring?.rrule && reservation.recurring?.start_date) {
    recurring = `DTSTART:${String(reservation.recurring.start_date).replace(/-/gi, "")}T080000Z\nRRULE:${reservation.recurring?.rrule}`;

    if (reservation.recurring?.end_date)
      recurring += `;UNTIL=${String(reservation.recurring.end_date).replace(/-/gi, "")}T080000Z`;
  }

  return recurring;
};

const ALLOW_INLINE_RESERVATION = false;
type Reservation = NonNullable<
  AiChatToolShowReservationInfoOutput["reservations"]
>[number];
const ChatBotToolShowReservationInfoCard: React.FC<{
  reservation: Reservation;
  isCompact: boolean;
  output: AiChatToolShowReservationInfoOutput;
  setSelectedReservation: (reservation: Reservation | null) => void;
  message?: TessaChatMessage | TessaResponseMessages | undefined;
  part?: TessaAiChatPart;
  messageState: UseTessaMessageStateReturn | null;
}> = ({
  reservation,
  isCompact,
  output,
  setSelectedReservation,
  message,
  part,
  messageState
}) => {
  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();
  const { queryClient, api } = useTacticHooks();
  const isWorkplaceRequestsAllowed =
    useWorkplaceRequestsAllowed("workplace_requests");
  const { createWorkplaceRequest, isLoading: isWorkplaceRequestLoading } =
    useWorkplaceRequestMutations();

  const askTessa = useAskTessa();
  const { openReserveModal, openDuplicateModal } = useReserve();
  const { openEditModal, openCancelModal } = useReserveModal();
  const { templates } = useI18n(authenticatedUser);
  const isLoading = useBooleanState();
  const wpRequestsRef = useRef<WorkplaceRequestsRef | null>(null);

  const resource = output._metadata?.resources_by_id?.[reservation.resource_id];

  const savedSchedules = messageState?.data?.data?.[resource?.id || ""] || [];
  const hasSchedules = savedSchedules.length > 0;

  const loadedSchedule = useScheduleById(savedSchedules[0] || null);

  const attenders = (reservation.user_ids || [])
    .map((userId) => {
      return output._metadata?.users_by_id?.[userId];
    })
    .filter((user) => !!user);

  if (!resource) return null;
  const isToolFullScreen = askTessa?.fullPageToolDisclosure.isOpen ?? false;
  const reservationOfficeId = resource?.office_id;
  const reservationParentId = resource.parent_id;
  const reservationType = resource.type;
  const startDate = new Date(dateStringToLocalTime(reservation.start));
  const duration = Math.floor(
    (new Date(reservation.end).getTime() -
      new Date(reservation.start).getTime()) /
      60000
  );

  const schedule =
    loadedSchedule.data ||
    ({
      id: "new-schedule",
      start: reservation.start,
      end: reservation.end,
      resource,
      slots: attenders.map((user) => ({
        user,
        resource
      })),
      description: "New reservation"
    } as ISchedule);

  const handleOpenReserveModal = async (reservation: Reservation) => {
    if (hasSchedules) {
      return;
    }

    const resource =
      output._metadata?.resources_by_id?.[reservation.resource_id];
    const reservationOfficeId =
      output._metadata?.resources_by_id?.[reservation.resource_id]?.office_id;
    const reservationParentId =
      output._metadata?.resources_by_id?.[reservation.resource_id]?.parent_id;
    const reservationType =
      output._metadata?.resources_by_id?.[reservation.resource_id]?.type;

    let workplaceRequestItems:
      | (WorkplaceRequestReserveFormData & { isNew: boolean })[]
      | undefined;

    if (isWorkplaceRequestsAllowed && reservation.workplace_request?.type_id) {
      const workplaceRequestTypes = isWorkplaceRequestsAllowed
        ? (await api.client.workplaceRequestTypes.getWorkplaceRequestTypes())
            .result?.elements || []
        : undefined;

      const type = workplaceRequestTypes?.find(
        (t) => t.id === reservation.workplace_request?.type_id
      );

      workplaceRequestItems = [
        {
          isNew: true,
          title: reservation.workplace_request.title,
          isDeleted: false,
          originalData: {
            office_id: reservation.workplace_request.office_id
          } as WorkplaceRequestElement,
          id: undefined,
          type: type || {
            id: reservation.workplace_request.type_id,
            name: "Unknown Type",
            description: "",
            // A request already exists against this type, so it files tickets
            // whatever the (unresolvable) real type says.
            kind: "default",
            in_use: false,
            llm_context: "",
            assigned_users: [],
            category_id: "",
            office_ids: [],
            has_office_restrictions: false,
            resource_ids: [],
            has_resource_restrictions: false,
            default_auto_assignee: null,
            office_auto_assignees: {},
            statuses: [],
            fields: [],
            icon: null,
            requires_approval: false,
            close_on_approval: false,
            requires_location: false,
            require_location_at_approval: false,
            hide_description: false
          },
          description: reservation.workplace_request.description
        }
      ];
    }

    if (!reservationType) return;
    const startDate = new Date(dateStringToLocalTime(reservation.start));
    const duration = Math.floor(
      (new Date(reservation.end).getTime() -
        new Date(reservation.start).getTime()) /
        60000
    );

    const owner = reservation.organizer_user_id
      ? ({ id: reservation.organizer_user_id } as IUserFull)
      : attenders?.length === 1 &&
          attenders[0]?.id !== authenticatedUser?.user?.id
        ? attenders[0]
        : undefined;

    const recurring = getRecurringString(reservation);

    openReserveModal({
      start: startDate,
      time: startDate,
      duration,
      floorId: reservationParentId,
      title: reservation.title,
      description: reservation.description,
      resourceType: reservationType,
      owner,
      office: reservationOfficeId
        ? ({
            id: reservationOfficeId
          } as Office)
        : undefined,
      resource: resource,
      attenders,
      onSuccess: () => {
        messageState?.setMessageState("completed", {
          data: {}
        });
      },
      workplaceRequestItems,
      isAdvancedRecurringOpenByDefault: !!recurring,
      recurring,
      onlineMeetingProvider:
        (reservation.online_meeting_provider as OnlineMeetingProvider) ||
        undefined
    });
  };

  const handleOpenEditModal = (schedule: ISchedule) => {
    openEditModal(schedule, true);
  };

  const handleOpenCancelModal = (schedule: ISchedule) => {
    openCancelModal([schedule]);
  };

  const reserveResource = async (reservation: Reservation) => {
    const resource =
      output._metadata?.resources_by_id?.[reservation.resource_id];

    const authUserId = authenticatedUser?.user?.id;
    const offices = authenticatedUser.offices?.offices ?? [];
    if (!resource || !authUserId) return;
    isLoading.true();
    const currentOfficeId = resource?.office_id;
    const reservationType = resource.type;
    const isMeetingRoom = reservationType === "meeting_room";
    const startDate = new Date(dateStringToLocalTime(reservation.start));
    const duration = Math.floor(
      (new Date(reservation.end).getTime() -
        new Date(reservation.start).getTime()) /
        60000
    );

    const office = offices.find((office) => office.id === currentOfficeId);
    if (!office) {
      isLoading.false();

      throw new Error(
        `Office with id ${currentOfficeId} not found in user offices`
      );
    }

    const data = {
      time: startDate,
      duration: { durationLabel: "", value: duration },
      resource: resource,
      users: attenders.map((user) => ({ value: user.id || "" })) || [],
      teams: [],
      recurring: getRecurringString(reservation) || "",
      organizer: reservation.organizer_user_id
        ? { value: reservation.organizer_user_id }
        : { value: "" },
      parking_resource: null,
      weekDates: [startDate],
      title: reservation.title,
      description: reservation.description,
      online_meeting: reservation.online_meeting_provider
        ? {
            provider:
              reservation.online_meeting_provider as OnlineMeetingProvider
          }
        : undefined
    };

    try {
      const ownerUserId = reservation.user_ids?.[0];
      const { hasError, message, scheduleIds } = await saveReservations(data, {
        organizerId: data.organizer?.value ?? null,
        isMeetingRoom,
        authUserId: ownerUserId || authUserId,
        office,
        // allowOthers && the admin has chosen a user
        allowOthers: true,
        timeZone: office.time_zone,
        officeId: currentOfficeId,
        noParkingResource,
        dateFormat: templates.displayDate,
        orgSlug: authenticatedUser.slug || ""
      });

      if (scheduleIds && scheduleIds.length > 0) {
        const scheduleId = scheduleIds[0];
        const workplaceRequestData = reservation.workplace_request;
        if (
          isWorkplaceRequestsAllowed &&
          scheduleId &&
          workplaceRequestData &&
          workplaceRequestData.type_id &&
          workplaceRequestData.office_id
        ) {
          const requestData: CreateWorkplaceRequestParams = {
            title: workplaceRequestData.title,
            description: workplaceRequestData.description,
            type_id: workplaceRequestData.type_id,
            office_id: workplaceRequestData.office_id,
            status: "open",
            schedule_id: scheduleId
          };

          try {
            await createWorkplaceRequest(requestData);
          } catch (error) {
            console.error("Failed to create workplace request:", error);
          }
        }

        messageState?.setMessageState("completed", {
          data: {
            ...messageState?.data?.data,
            [resource.id]: scheduleIds
          }
        });

        await invalidateReservationQueries(queryClient);
      }

      if (hasError && message) {
        toast.error(message);
      }
    } catch (err) {
      logError(err);
    } finally {
      isLoading.false();
    }
  };

  const checkInCard = hasSchedules ? (
    <CheckInCardOnWeb
      key={schedule.id}
      reservation={{
        ...schedule,
        resource: {
          ...resource,
          floor: {
            id: reservationParentId || "",
            name: ""
          },
          areas: []
        },
        joinUrl: ""
      }}
      onEdit={async () => {
        await handleOpenEditModal(schedule);
      }}
      onCancel={async () => {
        await handleOpenCancelModal(schedule);
      }}
      onDuplicate={async () => {
        setTimeout(() => {
          openDuplicateModal(schedule).then();
        }, 200);
      }}
    />
  ) : (
    <div className={"p-2"}>
      <ResourceLabel
        resource={resource}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          handleOpenReserveModal(reservation);
        }}
        schedules={[schedule]}
        allowFavorite={false}
        showAttenders
      />
    </div>
  );

  return (
    <div
      key={reservation.resource_id}
      className={cn(
        "min-w-[400px]",
        isCompact && "rounded-xl bg-neutral-50 p-2"
      )}
    >
      {!isCompact && !!reservationOfficeId && !!reservationParentId && (
        <div className={"rounded-xl bg-neutral-50 p-2"}>
          <Card
            className={
              "borer-neutral-300 w-full cursor-pointer hover:border-1 p-0 border-0"
            }
            contentProps={{ className: "p-0" }}
            hasShadow={false}
          >
            <div className={"mt-2 border-t border-t-neutral-200 px-0"}>
              {checkInCard}
            </div>
            <MapWithMiniOfficePhoto
              resourceId={reservation.resource_id}
              officeId={reservationOfficeId}
              parentId={reservationParentId}
              startDate={reservation.start.split("T")[0]}
              resourceImage={undefined}
              showMiniMap={false}
              addMainClassName={!isToolFullScreen}
              className={
                isToolFullScreen
                  ? "absolute inset-0 top-40 border-t"
                  : undefined
              }
            />
          </Card>
        </div>
      )}
      {!isCompact &&
        ALLOW_INLINE_RESERVATION &&
        !!reservationType &&
        !!reservationOfficeId && (
          <ReserveForm
            reserveModal={true}
            isEmbedded={false}
            onClose={() => null}
            start={startDate}
            time={startDate}
            resource={
              output._metadata?.resources_by_id?.[reservation.resource_id]
            }
            duration={duration}
            floorId={reservationParentId}
            resourceType={reservationType}
            office={
              {
                id: reservationOfficeId
              } as Office
            }
            attenders={attenders}
            wpRequestsRef={wpRequestsRef}
          />
        )}
      {isCompact && (
        <Card
          className={
            "borer-neutral-300 w-full cursor-pointer p-0 hover:border-1"
          }
          contentProps={{ className: "p-0" }}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            handleOpenReserveModal(reservation);
          }}
          hasShadow={!hasSchedules}
          isFlat={hasSchedules}
        >
          {checkInCard}
        </Card>
      )}
      {!hasSchedules &&
        !ALLOW_INLINE_RESERVATION &&
        !!reservationType &&
        !!reservationOfficeId && (
          <div className={"mt-4 flex w-full items-center"}>
            <Button
              className={"w-full"}
              onClick={() => reserveResource(reservation)}
              isLoading={isLoading.state}
            >
              Reserve
            </Button>
          </div>
        )}
    </div>
  );
};

export const ChatBotToolShowReservationInfo: React.FC<
  TessaMessageRendererProps<AiChatToolShowReservationInfoPart>
> = ({ part, message }) => {
  const output = JSON.parse(part?.output || "null") as
    | AiChatToolShowReservationInfoOutput
    | undefined;

  const askTessa = useAskTessa();

  const [selectedReservation, setSelectedReservation] =
    React.useState<Reservation | null>(null);

  const messageState = useTessaMessageState(
    askTessa?.selectedChatId,
    message,
    part
  );

  if (!output) return <TacticLoader className={"opacity-60"} />;

  const getReservation = (reservation: Reservation, isCompact = false) => {
    return (
      <ChatBotToolShowReservationInfoCard
        reservation={reservation}
        isCompact={isCompact}
        output={output}
        setSelectedReservation={setSelectedReservation}
        message={message}
        part={part}
        messageState={messageState}
      />
    );
  };

  const availableReservations =
    (output?.reservations || []).filter((reservation) => {
      return !messageState?.data?.data?.[reservation.resource_id]?.length;
    }) || [];

  const scheduledReservations =
    (output?.reservations || []).filter((reservation) => {
      return !!messageState?.data?.data?.[reservation.resource_id]?.length;
    }) || [];

  return (
    <ChatBotToolCard toolId={part.toolCallId}>
      {messageState?.state === "cancelled" && "Reservation cancelled."}
      {
        <>
          {output?.reservations?.length === 1 &&
            !selectedReservation &&
            getReservation(output.reservations[0])}
          {(output?.reservations?.length || 0) > 1 && selectedReservation && (
            <div>
              <div>
                <Button
                  variant={"link"}
                  className={"mb-4 p-0"}
                  onClick={() => setSelectedReservation(null)}
                >
                  &larr; Choose another resource
                </Button>
              </div>
              {getReservation(selectedReservation)}
            </div>
          )}
          {(output?.reservations?.length || 0) > 1 && !selectedReservation && (
            <div className={"w-full"}>
              <Heading as={"h3"} className={"mb-6"}>
                I found {output?.reservations?.length} suitable resources
              </Heading>
              {(scheduledReservations?.length || 0) > 0 && (
                <>
                  <div>You have reserved this resources</div>
                  <div
                    className={"grid w-full grid-cols-2 flex-wrap gap-4 py-4"}
                  >
                    {scheduledReservations.map((reservation) => {
                      return getReservation(reservation, true);
                    })}
                  </div>
                </>
              )}
              {(availableReservations?.length || 0) > 0 && (
                <>
                  <div>Available resources</div>
                  <div
                    className={"grid grid-cols-2 w-full flex-wrap gap-4 py-4"}
                  >
                    {availableReservations.map((reservation) => {
                      return getReservation(reservation, true);
                    })}
                  </div>
                </>
              )}
            </div>
          )}
        </>
      }
    </ChatBotToolCard>
  );
};
